import { useTranslation } from 'react-i18next';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';

import { AppLayout } from '@/app/layouts/AppLayout';
import { AuthShell } from '@/shared/components/AuthShell';
import { LoadingState } from '@/shared/components/States';
import { LoginAlias } from './LoginAlias';
import { OldBranchesAddress } from './OldBranchesAddress';
import { RequireAuth } from './RequireAuth';
import { ShellNotFound } from './ShellNotFound';
import { lazyRoute } from './lazyRoute';

/*
 * Every area below is lazily loaded, so the initial download is the shell plus
 * the dashboard only. The area a user actually opens is fetched on navigation.
 *
 *   organization    regions, warehouses, categories
 *   administration  users, roles, delegations, currencies, tax, settings, audit,
 *                   purchasing units
 *   suppliers       vendor master, qualification, framework agreements
 *   catalogue       guided buying, cart
 *   procurement     requests, approvals, purchase orders, receiving, invoices
 *   reports         dashboards, analytics, audit reporting
 *   vendor          the supplier-facing portal
 */

// One loader per area. Sharing the loader across an area's routes is what
// collapses that area into a single chunk instead of one chunk per page.
const authArea = () => import('@/features/auth/routes');
const dashboardArea = () => import('@/features/dashboard/routes');
const organizationArea = () => import('@/features/organization/routes');
const administrationArea = () => import('@/features/administration/routes');
const suppliersArea = () => import('@/features/suppliers/routes');
const catalogueArea = () => import('@/features/catalogue/routes');
const policyArea = () => import('@/features/policy/routes');
const procurementArea = () => import('@/features/procurement/routes');
const ordersArea = () => import('@/features/orders/routes');
const receivingArea = () => import('@/features/receiving/routes');
const sourcingArea = () => import('@/features/sourcing/routes');
const onboardingArea = () => import('@/features/onboarding/routes');
const reportsArea = () => import('@/features/reports/routes');
const vendorArea = () => import('@/features/vendor/routes');
const platformArea = () => import('@/features/platform/routes');
// PUX §1.8 — the signed-in person's own pages, one per portal (pux-acct).
const accountArea = () => import('@/features/account/routes');

function ActivationLoading() {
  const { t } = useTranslation();

  return (
    <AuthShell identity="tenant" title={t('activation.title')} subtitle={t('activation.subtitle')}>
      <LoadingState rows={3} label={t('activation.checking')} />
    </AuthShell>
  );
}

const router = createBrowserRouter([
  /*
   * PO-04 — a supplier acting straight from an email or a WhatsApp message.
   *
   * Outside every shell and every guard: most suppliers will never create a
   * portal account, and an order that can only be confirmed after a password
   * reset is one that sits unconfirmed while a branch waits for stock. The
   * TOKEN is the authority, it names one action against one order, and this
   * route deliberately offers nowhere else to navigate to.
   */
  { path: '/actions/:token', element: lazyRoute(vendorArea, 'SecureActionPage') },

  /*
   * Setting a password on an invited account.
   *
   * Public by necessity: the person following this link has no session and
   * cannot obtain one until they finish here, so it sits outside the
   * authenticated shell and outside the supplier shell. The token is the
   * authority and it names one account.
   */
  {
    path: '/activate/:token',
    element: lazyRoute(authArea, 'ActivationPage', <ActivationLoading />),
  },

  /*
   * `/login` — the address people type, and the one the website links to.
   *
   * An alias only: the sign-in itself stays in place at `/`, inside
   * `RequireAuth`, so there is still exactly one authentication path. A safe
   * internal `?next=` survives; anything else lands on `/` (see LoginAlias).
   */
  { path: '/login', element: <LoginAlias /> },

  /*
   * SUP-02 — the supplier portal, in its own shell.
   *
   * A supplier is not a user of the buying organisation: different guard, no
   * tenant permissions, and nowhere to navigate except their own orders.
   * Keeping the shells apart makes that boundary structural rather than a
   * matter of hiding menu items.
   */
  /*
   * S-040 (COM-19, local-review scope) — the Fidelen operator console, in its
   * own shell: the platform guard, platform permissions, its own session and
   * query keys. Not the tenant administrator's pages under another name, and no
   * route from here into any customer's procurement.
   */
  {
    path: '/platform',
    element: lazyRoute(platformArea, 'PlatformLayout'),
    children: [
      { index: true, element: lazyRoute(platformArea, 'PlatformOverviewPage') },
      { path: 'organisations', element: lazyRoute(platformArea, 'PlatformOrganisationsPage') },
      {
        path: 'organisations/new',
        element: lazyRoute(platformArea, 'PlatformNewOrganisationPage'),
      },
      { path: 'organisations/:id', element: lazyRoute(platformArea, 'PlatformOrganisationPage') },
      { path: 'commercial', element: <Navigate to="/platform/commercial/plans" replace /> },
      { path: 'commercial/plans', element: lazyRoute(platformArea, 'PlatformPlansPage') },
      { path: 'commercial/plans/:id', element: lazyRoute(platformArea, 'PlatformPlansPage') },
      {
        path: 'commercial/subscriptions',
        element: lazyRoute(platformArea, 'PlatformSubscriptionsPage'),
      },
      {
        path: 'commercial/subscriptions/:id',
        element: lazyRoute(platformArea, 'PlatformSubscriptionsPage'),
      },
      { path: 'commercial/capacity', element: lazyRoute(platformArea, 'PlatformCapacityPage') },
      { path: 'commercial/addons', element: lazyRoute(platformArea, 'PlatformAddonsPage') },
      { path: 'operations', element: lazyRoute(platformArea, 'PlatformOperationsPage') },
      { path: 'audit', element: lazyRoute(platformArea, 'PlatformAuditPage') },
      // Leads contract v1 §5 — Book a Demo enquiries: prospect contacts, never tenants.
      { path: 'leads', element: lazyRoute(platformArea, 'PlatformLeadsPage') },
      { path: 'leads/:id', element: lazyRoute(platformArea, 'PlatformLeadDetailPage') },
      // Integrations — the owner's Zoho CRM connection (platform.integration.manage only).
      {
        path: 'integrations',
        element: <Navigate to="/platform/integrations/zoho" replace />,
      },
      {
        path: 'integrations/zoho',
        element: lazyRoute(platformArea, 'PlatformZohoIntegrationPage'),
      },
      // MFA — the operator's own sign-in security.
      { path: 'security', element: lazyRoute(platformArea, 'PlatformSecurityPage') },
      // PRF-02 — the operator's own account, read-only.
      { path: 'account', element: lazyRoute(accountArea, 'PlatformAccountPage') },
      // PLAT-P2-03 — a mistyped console address stays in the console.
      {
        path: '*',
        element: <ShellNotFound home="/platform" homeLabelKey="platform.nav.overview" />,
      },
    ],
  },

  {
    path: '/vendor',
    element: lazyRoute(vendorArea, 'VendorLayout'),
    children: [
      // SUP-02 — the supplier's home: what needs them now.
      { index: true, element: lazyRoute(vendorArea, 'VendorHomePage') },
      { path: 'orders', element: lazyRoute(vendorArea, 'VendorOrdersPage') },
      { path: 'orders/:id', element: lazyRoute(vendorArea, 'VendorOrderDetailPage') },
      // SRC-05 — the supplier's own sourcing invitations.
      { path: 'rfqs', element: lazyRoute(vendorArea, 'VendorRfqsPage') },
      { path: 'rfqs/:id', element: lazyRoute(vendorArea, 'VendorRfqPage') },
      // SUP-06 — the agreements this supplier is party to, and its own record.
      { path: 'agreements', element: lazyRoute(vendorArea, 'VendorAgreementsPage') },
      { path: 'agreements/:id', element: lazyRoute(vendorArea, 'VendorAgreementPage') },
      { path: 'profile', element: lazyRoute(vendorArea, 'VendorProfilePage') },
      // IA-11 / PRF-02 — the supplier person's own account ("Company details" is profile).
      { path: 'account', element: lazyRoute(accountArea, 'VendorAccountPage') },
      // NEW-A2 — a mistyped supplier address stays in the supplier portal.
      { path: '*', element: <ShellNotFound home="/vendor" homeLabelKey="vendor.home.nav" /> },
    ],
  },

  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: lazyRoute(dashboardArea, 'DashboardPage') },

      // MFA — one's own sign-in security (authenticator, recovery codes).
      { path: 'account/security', element: lazyRoute(authArea, 'SecurityPage') },
      // PRF-02 / PRF-04 — one's own profile and preferences; /profile is its alias.
      { path: 'account/profile', element: lazyRoute(accountArea, 'ProfilePage') },
      { path: 'profile', element: <Navigate to="/account/profile" replace /> },

      // ── Organisation ───────────────────────────────────────────────────────
      { path: 'organization/regions', element: lazyRoute(organizationArea, 'RegionsPage') },
      /*
       * DEF-035 — the separate "Purchasing nodes" page is gone: the Purchasing
       * Unit is the one place a purchasing place is set up (client instruction,
       * 9 October 2026). The old address, which people have bookmarked and old
       * links still carry, goes to it for a reader who may see the units, and
       * to their own profile for anybody else (`OldBranchesAddress`). Nothing
       * behind it moved: the records, their ids, their links and the API are
       * what they were.
       */
      { path: 'organization/branches', element: <OldBranchesAddress /> },
      { path: 'organization/warehouses', element: lazyRoute(organizationArea, 'WarehousesPage') },
      { path: 'organization/categories', element: lazyRoute(organizationArea, 'CategoriesPage') },

      // ── Catalogue (CAT-01, CAT-03, CAT-04) ─────────────────────────────────
      { path: 'catalogue', element: lazyRoute(catalogueArea, 'CataloguePage') },
      { path: 'catalogue/:id', element: lazyRoute(catalogueArea, 'CatalogueItemPage') },
      // CAT-01 — HQ catalogue administration. A different job from browsing:
      // what exists, where it comes from, and who may see it.
      { path: 'admin/catalogue', element: lazyRoute(catalogueArea, 'CatalogueAdminPage') },

      // ── Purchase requests and approvals (Epic 4) ───────────────────────────
      { path: 'cart', element: lazyRoute(procurementArea, 'CartPage') },
      { path: 'my/requests', element: lazyRoute(procurementArea, 'MyRequestsPage') },
      {
        path: 'requests/off-catalogue/new',
        element: lazyRoute(procurementArea, 'OffCataloguePage'),
      },
      { path: 'requests/:id', element: lazyRoute(procurementArea, 'RequestDetailPage') },
      // REQ-05 — amending a RETURNED request is the same screen as the cart.
      { path: 'requests/:id/edit', element: lazyRoute(procurementArea, 'CartPage') },
      { path: 'approvals', element: lazyRoute(procurementArea, 'ApprovalsPage') },

      // ── Purchase orders (Epic 5) ───────────────────────────────────────────
      { path: 'purchase-orders', element: lazyRoute(ordersArea, 'PurchaseOrdersPage') },
      { path: 'purchase-orders/:id', element: lazyRoute(ordersArea, 'PurchaseOrderDetailPage') },
      { path: 'approvals/:id', element: lazyRoute(procurementArea, 'ApprovalDecisionPage') },

      // ── Receiving (RCV-03…RCV-06) ─────────────────────────────────────────
      { path: 'receiving', element: lazyRoute(receivingArea, 'ReceivingQueuePage') },
      { path: 'receiving/:id', element: lazyRoute(receivingArea, 'ReceiveOrderPage') },

      // ── Sourcing (SRC-01…SRC-12) ──────────────────────────────────────────
      // ONB-01 / ONB-02 / CAT-09 — R-032 S-039 "Imports / Onboarding".
      { path: 'onboarding', element: lazyRoute(onboardingArea, 'OnboardingPage') },
      { path: 'onboarding/imports/:id', element: lazyRoute(onboardingArea, 'ImportDetailPage') },
      { path: 'sourcing', element: lazyRoute(sourcingArea, 'SourcingQueuePage') },
      // Before `sourcing/:id`, or "new" is read as an RFQ identifier.
      { path: 'sourcing/new', element: lazyRoute(sourcingArea, 'StartRfqPage') },
      { path: 'sourcing/:id', element: lazyRoute(sourcingArea, 'RfqDetailPage') },

      // ── Reporting and the export centre (RPT-01…RPT-08, RPT-11) ───────────
      // `records` before `:id` so the literal segment is never read as a
      // report identifier.
      // GS-24 — one master–detail screen: the report rail stays beside the
      // hub, a report and its records. Same paths, so deep links keep working.
      {
        path: 'reports',
        element: lazyRoute(reportsArea, 'ReportsLayout'),
        children: [
          { index: true, element: lazyRoute(reportsArea, 'ReportsHubPage') },
          { path: 'records/:dataset', element: lazyRoute(reportsArea, 'RecordsPage') },
          { path: ':id', element: lazyRoute(reportsArea, 'ReportPage') },
        ],
      },

      // ── Suppliers and agreements (SUP-01, SUP-04, SUP-06, SUP-02) ──────────
      { path: 'suppliers', element: lazyRoute(suppliersArea, 'SuppliersPage') },
      // FORM-02 — adding and editing a supplier is a page of its own (TASK §7.3).
      // `new` before `:id`, so the literal segment is never read as an identifier.
      { path: 'suppliers/new', element: lazyRoute(suppliersArea, 'SupplierFormPage') },
      { path: 'suppliers/:id', element: lazyRoute(suppliersArea, 'SupplierDetailPage') },
      { path: 'suppliers/:id/edit', element: lazyRoute(suppliersArea, 'SupplierFormPage') },
      { path: 'agreements', element: lazyRoute(suppliersArea, 'AgreementsPage') },
      { path: 'agreements/:id', element: lazyRoute(suppliersArea, 'AgreementDetailPage') },

      // ── Budget and approval policy (Epic 3) ────────────────────────────────
      { path: 'budgets', element: lazyRoute(policyArea, 'BudgetsPage') },
      { path: 'budgets/:id', element: lazyRoute(policyArea, 'BudgetLedgerPage') },
      { path: 'admin/approval-rules', element: lazyRoute(policyArea, 'ApprovalRulesPage') },
      {
        path: 'admin/approval-rules/new',
        element: lazyRoute(policyArea, 'ApprovalRuleBuilderPage'),
      },
      {
        path: 'admin/approval-rules/simulator',
        element: lazyRoute(policyArea, 'ApprovalSimulatorPage'),
      },
      {
        path: 'admin/approval-rules/:id',
        element: lazyRoute(policyArea, 'ApprovalRuleBuilderPage'),
      },

      // ── Administration ─────────────────────────────────────────────────────
      { path: 'admin/users', element: lazyRoute(administrationArea, 'UsersPage') },
      { path: 'admin/roles', element: lazyRoute(administrationArea, 'RolesPage') },
      { path: 'admin/delegations', element: lazyRoute(administrationArea, 'DelegationsPage') },
      { path: 'admin/currencies', element: lazyRoute(administrationArea, 'CurrenciesPage') },
      { path: 'admin/tax-codes', element: lazyRoute(administrationArea, 'TaxCodesPage') },
      { path: 'admin/settings', element: lazyRoute(administrationArea, 'SettingsPage') },
      {
        path: 'admin/organization',
        element: lazyRoute(administrationArea, 'OrganizationProfilePage'),
      },
      { path: 'admin/audit', element: lazyRoute(administrationArea, 'AuditPage') },
      {
        path: 'admin/purchasing-units',
        element: lazyRoute(administrationArea, 'PurchasingUnitsPage'),
      },

      { path: '*', element: lazyRoute(dashboardArea, 'NotFoundPage') },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
